import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useI18n } from '../../i18n';
import { useStore } from '../../store/useStore';
import { FiUpload, FiX, FiFile, FiFileText, FiCheck } from 'react-icons/fi';
import { Book, FileType } from '../../types';
import { saveEPUBFile, savePDFFile, saveCoverImage } from '../../utils/fileStorage';
import { generateCoverImage } from '../../utils/coverGenerator';
import ePub from 'epubjs';
import * as pdfjsLib from 'pdfjs-dist';

// PDF.js worker 설정
pdfjsLib.GlobalWorkerOptions.workerSrc = `${import.meta.env.BASE_URL}pdf.worker.min.mjs`;

interface UploadModalProps {
  onClose: () => void;
  initialFile?: File | null;
}

const UploadModal = ({ onClose, initialFile }: UploadModalProps) => {
  const { t } = useI18n();
  const { addBook } = useStore();
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [author, setAuthor] = useState('');
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // initialFile이 있으면 자동 처리
  useEffect(() => {
    if (initialFile) {
      processFile(initialFile);
    }
  }, [initialFile]);

  // ✅ 파일 선택 즉시 메타데이터 추출
  const processFile = async (selectedFile: File) => {
    if (!selectedFile.name.endsWith('.epub') && !selectedFile.name.endsWith('.pdf')) {
      alert(t('library.fileTypeError'));
      return;
    }

    setFile(selectedFile);
    setExtracting(true);

    const fallbackTitle = selectedFile.name.replace(/\.(epub|pdf)$/i, '');

    try {
      if (selectedFile.name.endsWith('.epub')) {
        // ✅ EPUB 메타데이터 자동 추출
        const arrayBuffer = await selectedFile.arrayBuffer();
        const epubBook = ePub(arrayBuffer);

        await Promise.race([
          epubBook.ready,
          new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 10000))
        ]);

        // 제목 추출
        const metadata = await epubBook.loaded.metadata;
        if (metadata?.title) setTitle(metadata.title);
        else setTitle(fallbackTitle);

        // 저자 추출
        if (metadata?.creator) setAuthor(metadata.creator);

        // 표지 이미지 추출
        try {
          const coverUrl = await Promise.race([
            epubBook.coverUrl(),
            new Promise<null>((_, reject) => setTimeout(() => reject(new Error('timeout')), 8000))
          ]);
          if (coverUrl) {
            setCoverPreview(coverUrl as string);
          }
        } catch {
          console.warn('표지 추출 실패');
        }

        epubBook.destroy();
        console.log('✅ EPUB 메타데이터 추출 완료');

      } else {
        // ✅ PDF 메타데이터 추출
        setTitle(fallbackTitle);

        const arrayBuffer = await selectedFile.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

        // PDF 메타데이터에서 제목/저자 시도
        const pdfMeta = await pdf.getMetadata();
        const info = pdfMeta?.info as any;
        if (info?.Title) setTitle(info.Title);
        if (info?.Author) setAuthor(info.Author);

        // 첫 페이지를 표지 미리보기로
        try {
          const page = await pdf.getPage(1);
          const viewport = page.getViewport({ scale: 0.5 });
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');
          if (context) {
            canvas.width = viewport.width;
            canvas.height = viewport.height;
            // SPRINT 1.11: 오프스크린 캔버스라 hidden 탭에서 rAF가 멈추면 이 렌더도
            // 영원히 pending된다 — Reader.tsx 플립북 추출과 정확히 같은 결함이다.
            // 업로드 중 다른 탭으로 전환하면 "표지 미리보기가 안 뜬다"로 나타난다.
            // 근거·수치는 Reader.tsx 렌더부 주석 참고(PM 실브라우저 A/B 실측).
            await page.render({ canvasContext: context, viewport, intent: 'print' } as any).promise;
            setCoverPreview(canvas.toDataURL('image/jpeg', 0.7));
          }
        } catch {
          console.warn('PDF 표지 미리보기 실패');
        }

        pdf.destroy();
        console.log('✅ PDF 메타데이터 추출 완료');
      }
    } catch (err) {
      console.warn('메타데이터 추출 실패, 파일명 사용:', err);
      if (!title) setTitle(fallbackTitle);
    } finally {
      setExtracting(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) processFile(selectedFile);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) processFile(droppedFile);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file || !title) {
      alert(t('library.fillAllFields'));
      return;
    }

    setUploading(true);

    try {
      const bookId = `book-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
      const fileType: FileType = file.name.endsWith('.pdf') ? 'pdf' : 'epub';
      const authorName = author || '알 수 없는 저자';

      console.log(`📚 책 추가 시작: ${bookId} (${fileType.toUpperCase()})`);

      let coverUrl: string | undefined = undefined;
      let totalPages: number | undefined = undefined;

      if (fileType === 'epub') {
        setUploadStatus('📥 EPUB 파일 저장 중...');
        await saveEPUBFile(bookId, file);

        // 커버 이미지 저장
        let coverExtracted = false;
        try {
          setUploadStatus('🖼️ 표지 이미지 저장 중...');
          const arrayBuffer = await file.arrayBuffer();
          const epubBook = ePub(arrayBuffer);
          await Promise.race([
            epubBook.ready,
            new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 10000))
          ]);

          const cover = await Promise.race([
            epubBook.coverUrl(),
            new Promise<null>((_, reject) => setTimeout(() => reject(new Error('timeout')), 10000))
          ]);

          if (cover) {
            const response = await fetch(cover);
            const coverBlob = await response.blob();
            await saveCoverImage(bookId, coverBlob);
            coverUrl = `indexed-db-cover://${bookId}`;
            coverExtracted = true;
          }
          epubBook.destroy();
        } catch (coverError) {
          console.warn('커버 추출 실패:', coverError);
        }

        if (!coverExtracted) {
          try {
            setUploadStatus('🎨 커버 이미지 생성 중...');
            const generatedCover = await generateCoverImage(title, authorName);
            await saveCoverImage(bookId, generatedCover);
            coverUrl = `indexed-db-cover://${bookId}`;
          } catch (genError) {
            console.warn('커버 자동 생성 실패:', genError);
          }
        }

      } else {
        setUploadStatus('📥 PDF 파일 저장 중...');
        await savePDFFile(bookId, file);

        try {
          setUploadStatus('📄 PDF 정보 추출 중...');
          const arrayBuffer = await file.arrayBuffer();
          const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
          totalPages = pdf.numPages;

          try {
            const page = await pdf.getPage(1);
            const viewport = page.getViewport({ scale: 1.0 });
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            if (context) {
              canvas.height = viewport.height;
              canvas.width = viewport.width;
              // SPRINT 1.11: 위 표지 미리보기와 동일 — 오프스크린이라 hidden 탭에서
              // rAF 정지에 걸린다. 여기서 멈추면 책은 추가되는데 표지만 영영 안 생긴다.
              await page.render({ canvasContext: context, viewport, intent: 'print' } as any).promise;
              const blob = await new Promise<Blob>((resolve) => {
                canvas.toBlob((blob) => resolve(blob!), 'image/jpeg', 0.8);
              });
              await saveCoverImage(bookId, blob);
              coverUrl = `indexed-db-cover://${bookId}`;
            }
          } catch (coverError) {
            console.warn('PDF 커버 생성 실패:', coverError);
            const generatedCover = await generateCoverImage(title, authorName);
            await saveCoverImage(bookId, generatedCover);
            coverUrl = `indexed-db-cover://${bookId}`;
          }
        } catch (pdfError) {
          console.warn('PDF 정보 추출 실패:', pdfError);
        }
      }

      setUploadStatus('✅ 완료!');

      const newBook: Book = {
        id: bookId,
        title,
        author: authorName,
        cover: coverUrl,
        filePath: bookId,
        fileType,
        progress: 0,
        addedDate: new Date(),
        status: 'not-started',
        totalPages,
      };

      addBook(newBook);
      onClose();
    } catch (error) {
      console.error('❌ 책 추가 실패:', error);
      alert(t('library.uploadError'));
    } finally {
      setUploading(false);
      setUploadStatus('');
    }
  };

  return createPortal(
    <div
      style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        width: '100vw', height: '100vh',
        background: 'rgba(0,0,0,0.6)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 99999,
        padding: '16px',
      }}
      onClick={(e) => { if (e.target === e.currentTarget && !uploading) onClose(); }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-lg w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t('library.addBook')}
          </h2>
          <button
            onClick={onClose}
            disabled={uploading}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <FiX size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* File Upload / 표지 미리보기 */}
          {!file ? (
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-10 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 dark:hover:bg-blue-900/10 transition-all mb-5"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".epub,.pdf"
                onChange={handleFileSelect}
                className="hidden"
              />
              <FiUpload size={48} className="mx-auto mb-4 text-gray-400" />
              <p className="text-gray-600 dark:text-gray-400 mb-2 text-lg font-medium">
                {t('library.dragDropText')}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-500">
                EPUB / PDF
              </p>
            </div>
          ) : (
            <div className="flex gap-4 mb-5 p-4 bg-gray-50 dark:bg-gray-900 rounded-xl">
              {/* 표지 미리보기 */}
              <div className="w-24 h-32 flex-shrink-0 bg-gray-200 dark:bg-gray-700 rounded-lg overflow-hidden shadow-md">
                {coverPreview ? (
                  <img src={coverPreview} alt="표지" className="w-full h-full object-cover" />
                ) : extracting ? (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full" />
                  </div>
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    {file.name.endsWith('.pdf') ? <FiFileText size={32} /> : <FiFile size={32} />}
                  </div>
                )}
              </div>

              {/* 파일 정보 */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 font-medium">
                    {file.name.endsWith('.pdf') ? 'PDF' : 'EPUB'}
                  </span>
                  <FiCheck size={14} className="text-green-500" />
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 truncate mb-1">{file.name}</p>
                <p className="text-xs text-gray-400">{(file.size / 1024 / 1024).toFixed(1)} MB</p>
                <button
                  type="button"
                  onClick={() => {
                    setFile(null);
                    setTitle('');
                    setAuthor('');
                    setCoverPreview(null);
                  }}
                  className="text-xs text-red-500 hover:text-red-600 mt-2"
                >
                  다른 파일 선택
                </button>
              </div>
            </div>
          )}

          {/* Title */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              {t('library.title')} {extracting && <span className="text-xs text-blue-500 ml-1">자동 추출 중...</span>}
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t('library.titlePlaceholder')}
              required
            />
          </div>

          {/* Author */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              {t('library.author')} {extracting && <span className="text-xs text-blue-500 ml-1">자동 추출 중...</span>}
            </label>
            <input
              type="text"
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t('library.authorPlaceholder') || '자동 추출됩니다'}
            />
          </div>

          {/* Upload Status */}
          {uploadStatus && (
            <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <p className="text-sm text-blue-700 dark:text-blue-300 text-center">
                {uploadStatus}
              </p>
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={uploading}
              className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
            >
              {t('library.cancel')}
            </button>
            <button
              type="submit"
              disabled={uploading || extracting || !file || !title}
              className="flex-1 px-4 py-2.5 gradient-primary text-white rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? t('library.processing') : t('library.add')}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default UploadModal;
